import { useState, useEffect } from 'react';
import { TrendingUp, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CURRENCIES = [
  { code: 'NGN', name: 'Nigerian Naira', flag: '🇳🇬' },
  { code: 'KES', name: 'Kenyan Shilling', flag: '🇰🇪' },
  { code: 'ZAR', name: 'South African Rand', flag: '🇿🇦' },
  { code: 'GHS', name: 'Ghanaian Cedi', flag: '🇬🇭' },
  { code: 'EGP', name: 'Egyptian Pound', flag: '🇪🇬' },
  { code: 'MAD', name: 'Moroccan Dirham', flag: '🇲🇦' },
  { code: 'ETB', name: 'Ethiopian Birr', flag: '🇪🇹' },
  { code: 'XOF', name: 'CFA Franc (West)', flag: '🌍' },
];

export default function ExchangeRateTicker() {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchRates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/trade/exchange-rates`);
      if (res.ok) {
        const data = await res.json();
        setRates(data.rates);
        setLastUpdated(new Date());
      }
    } catch (e) {
      void e; // Keep existing rates as fallback
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
    const interval = setInterval(fetchRates, 300000); // Refresh every 5 min
    return () => clearInterval(interval);
  }, []);

  if (loading || !rates) {
    return null; // Don't show anything while loading
  }

  return (
    <div className="bg-charcoal border-b border-gray-800" data-testid="exchange-rate-ticker">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center py-2 overflow-hidden">
          <div className="flex items-center gap-2 text-gold text-xs font-semibold mr-4 shrink-0">
            <TrendingUp className="w-4 h-4" />
            <span>USD RATES</span>
          </div>
          
          <div className="flex gap-6 animate-marquee whitespace-nowrap">
            {CURRENCIES.map(({ code, flag }) => (
              <div key={code} className="flex items-center gap-2 text-sm">
                <span>{flag}</span>
                <span className="text-gray-400">{code}</span>
                <span className="text-white font-medium">
                  {rates[code] ? rates[code].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                </span>
              </div>
            ))}
            {/* Duplicate for seamless scroll */}
            {CURRENCIES.map(({ code, flag }) => (
              <div key={`${code}-dup`} className="flex items-center gap-2 text-sm">
                <span>{flag}</span>
                <span className="text-gray-400">{code}</span>
                <span className="text-white font-medium">
                  {rates[code] ? rates[code].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                </span>
              </div>
            ))}
          </div>

          <button 
            onClick={fetchRates}
            className="ml-4 text-gray-500 hover:text-gold transition-colors shrink-0"
            title="Refresh rates"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>
      
      <style jsx="true">{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
